import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiResponse, PaginatedApiResponse } from '@card-software/nexus-utilities';
import { environment } from '@environments/environment';
import { LotExcelImportContext, LotExcelImportPreview } from '@app/models/inventory/lot-excel-import';
import { LotItem, LotSearchCommand, ShipmentLotUpsertCommand } from '@app/models/inventory/lot-item';
import { LotItemGroupedSearchCommand, LotItemManufacturerLotGroup } from '@app/models/inventory/lot-item-manufacturer-lot-group';
import { InvoiceLotAction, DeliveryNoteBatchCommand } from '@app/models/shipment/shipment';

@Injectable({
  providedIn: 'root',
})
export class InventoryLotProxyService {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = environment.proxyUrls.inventoryApi + '/api/v1/lot-item';

  public getInventoryLotByLotId(barcode: string): Observable<ApiResponse<LotItem>> {
    return this.httpClient.get<ApiResponse<LotItem>>(`${this.baseUrl}/lotId/${barcode}`);
  }

  public batchAddInventoryLots(body: InvoiceLotAction[]): Observable<ApiResponse<LotItem[]>> {
    return this.httpClient.post<ApiResponse<LotItem[]>>(`${this.baseUrl}/batch`, body);
  }

  public batchUpsertLotsByShipment(command: ShipmentLotUpsertCommand): Observable<ApiResponse<LotItem[]>> {
    return this.httpClient.post<ApiResponse<LotItem[]>>(`${this.baseUrl}/batch/consignments`, command);
  }

  public getLotsByDeliveryNoteId(deliveryNoteId: string): Observable<ApiResponse<LotItem[]>> {
    return this.httpClient.get<ApiResponse<LotItem[]>>(`${this.baseUrl}/delivery-note-reference/${deliveryNoteId}`);
  }

  public getLotsByInvoiceId(invoiceId: string, materializeLots = false, includeSplitLotItems = true): Observable<ApiResponse<LotItem[]>> {
    return this.httpClient.get<ApiResponse<LotItem[]>>(
      `${this.baseUrl}/invoice-reference/${invoiceId}?materializeLots=${materializeLots}&includeSplitLotItems=${includeSplitLotItems}`,
    );
  }

  public deleteLotItem(lotItemReference: string): Observable<ApiResponse<boolean>> {
    return this.httpClient.delete<ApiResponse<boolean>>(`${this.baseUrl}/${lotItemReference}`);
  }

  public search(command: LotSearchCommand, includeSplitLotItems = true): Observable<ApiResponse<PaginatedApiResponse<LotItem>>> {
    return this.httpClient.post<ApiResponse<PaginatedApiResponse<LotItem>>>(
      `${this.baseUrl}/search?includeSplitLotItems=${includeSplitLotItems}`,
      command,
    );
  }

  public searchGroupedByManufacturerLot(command: LotItemGroupedSearchCommand): Observable<ApiResponse<LotItemManufacturerLotGroup[]>> {
    return this.httpClient.post<ApiResponse<LotItemManufacturerLotGroup[]>>(`${this.baseUrl}/search/grouped-by-manufacturer-lot`, command);
  }

  public batchAddDeliveryNoteLots(command: DeliveryNoteBatchCommand): Observable<ApiResponse<LotItem[]>> {
    return this.httpClient.post<ApiResponse<LotItem[]>>(`${this.baseUrl}/batch/delivery-notes`, command);
  }

  public previewExcelImport(file: File, context: LotExcelImportContext): Observable<ApiResponse<LotExcelImportPreview>> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    let params = new HttpParams().set('itemCode', context.itemCode);
    if (context.invoiceReference) params = params.set('invoiceReference', context.invoiceReference);
    if (context.purchaseOrderReference) params = params.set('purchaseOrderReference', context.purchaseOrderReference);
    if (context.deliveryNoteReference) params = params.set('deliveryNoteReference', context.deliveryNoteReference);
    return this.httpClient.post<ApiResponse<LotExcelImportPreview>>(`${this.baseUrl}/import-excel/preview`, formData, { params });
  }
}
